import { useTranslate } from "@refinedev/core";
import { Button, Flex, Popconfirm, Typography } from "antd";
import { PdfHighlightsViewer } from "components/PdfHighlightsViewer/";
import React, { useMemo } from "react";
import { Chat } from "../../components/chat/Chat";
import { useProjectInstance } from "../hooks/useProjectInstance";
import { useRegenerateMedia } from "../hooks/useRegenerateMedia";
import { ExperienceLabel } from "../types";
import { ExperienceTypeSelector } from "./ExperienceTypeSelector";
import { ProjectVideo } from "./ProjectVideo";
import { VideoMetadata, IProjectDetails } from "./types";
import { isProjectHeyGenius } from "../helpers/helper";
import { radius } from "@libertify/ui";

export interface ProjectPreviewProps {
  backgroundImg: string;
  videoMetadata: VideoMetadata;
  videoRef: React.RefObject<HTMLVideoElement>;
  refetchProjectDetail: () => void;
  projectDetails: IProjectDetails;
}

export const ProjectPreview = ({
  projectDetails,
  backgroundImg,
  videoMetadata,
  videoRef,
  refetchProjectDetail,
}: ProjectPreviewProps) => {
  const t = useTranslate();

  const { instanceSettings } = useProjectInstance({
    projectId: projectDetails.projectId,
    instanceId: projectDetails.instanceId,
    mediaId: projectDetails.mediaId,
  });

  const experienceOptions = useMemo<Partial<Record<ExperienceLabel, boolean>>>(
    () => ({
      [ExperienceLabel.Video]: true,
      [ExperienceLabel.AnimatedDocument]:
        instanceSettings?.include_document_animations ?? false,
      [ExperienceLabel.Chatbot]: instanceSettings?.chat_mode !== "Disabled",
    }),
    [instanceSettings]
  );

  const { handleRegenerateMedia, isRegenerating } = useRegenerateMedia(
    refetchProjectDetail,
    projectDetails.mediaId,
    projectDetails.projectId,
    projectDetails.mediaStatus
  );

  const docsIds =
    projectDetails.project.documents?.map((doc: any) => doc.id) ?? [];
  const primaryDocument = projectDetails.project.documents?.[0];
  const documentUrl = primaryDocument?.path;
  const hasAnimatedHighlighter =
    experienceOptions[ExperienceLabel.AnimatedDocument];
  const hasChatbot = experienceOptions[ExperienceLabel.Chatbot];

  const pdfDocument = useMemo(() => {
    if (!primaryDocument || !documentUrl) return null;
    return { ...primaryDocument, url: documentUrl };
  }, [primaryDocument, documentUrl]);

  const videoContainerStyle = useMemo(
    () => ({
      maxWidth: hasAnimatedHighlighter ? "50%" : "100%",
      width: "fit-content", // Match the video's aspect ratio
    }),
    [hasAnimatedHighlighter]
  );

  const showExperienceTypeComponent = useMemo(() => {
    return (
      !isProjectHeyGenius(projectDetails.project.type) && !projectDetails.isLive
    );
  }, [projectDetails.project.type, projectDetails.isLive]);

  const canRegenerateMedia = useMemo(() => {
    return projectDetails.mediaStatus === "Generated";
  }, [projectDetails.mediaStatus]);

  return (
    <Flex vertical gap={10}>
      {showExperienceTypeComponent && (
        <ExperienceTypeSelector
          experienceOptions={experienceOptions}
          projectId={projectDetails.projectId}
          instanceId={projectDetails.instanceId}
          mediaId={projectDetails.mediaId}
        />
      )}
      <Flex gap={10} justify="center" align="stretch">
        <Flex align="center" justify="center" style={videoContainerStyle}>
          <ProjectVideo
            videoUrl={projectDetails.videoUrl}
            backgroundImg={backgroundImg}
            videoMetadata={videoMetadata}
            videoRef={videoRef}
          />
        </Flex>
        {hasAnimatedHighlighter && (
          <div
            style={{
              position: "relative",
              flex: 1,
              minHeight: "clamp(300px, 50vh, 600px)",
              borderRadius: radius.box,
              border: "1px solid #e0e0e0",
              overflow: "hidden",
            }}
          >
            {documentUrl && primaryDocument && (
              <PdfHighlightsViewer
                document={pdfDocument}
                startScrollTo=""
                pdfScaleValue="page-fit"
              />
            )}
          </div>
        )}
      </Flex>
      {canRegenerateMedia && (
        <Flex vertical align="center">
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 11,
              lineHeight: 1.1,
              textAlign: "center",
              marginBottom: "12px",
            }}
          >
            {t("media.projects.detail.regenerateVideoHint")}
          </Typography.Text>
          <Popconfirm
            title={t("buttons.confirm")}
            onConfirm={() => {
              void handleRegenerateMedia();
            }}
          >
            <Button
              style={{ maxWidth: "175px", marginBottom: 10 }}
              size="middle"
              shape="round"
              loading={isRegenerating}
              disabled={isRegenerating}
            >
              {t("media.projects.detail.regenerateVideo")}
            </Button>
          </Popconfirm>
        </Flex>
      )}
      {hasChatbot && (
        <Chat
          documentIds={docsIds}
          organizationId={projectDetails.project.organization_id}
          showEmptyMessages={false}
          showDot={false}
        />
      )}
    </Flex>
  );
};
